import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { getHomeMarketRegime } from '../api';

const FAMILY_COLORS = {
  growth_inflation: '#22c55e',
  volatility_risk: '#f59e0b',
  liquidity: '#3b82f6',
};

const TIMESCALE_OPTIONS = [
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '3Y', days: 1095 },
  { label: '5Y', days: 1825 },
  { label: '10Y', days: 3650 },
];

const PLAYBACK_SPEED_OPTIONS = [
  { label: '0.5x', value: 0.5 },
  { label: '1x', value: 1 },
  { label: '2x', value: 2 },
  { label: '4x', value: 4 },
];

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

function formatProbabilityPercent(value, digits = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'n/a';
  const clamped = clamp01(numeric);
  if (clamped >= 0.99995) return '100.0%';
  const shown = Math.min(clamped, 0.999);
  return `${(shown * 100).toFixed(digits)}%`;
}

function normalizeSigned(value, span) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || span <= 0) return 0.5;
  return clamp01((numeric + span) / (span * 2));
}

function truncateLabel(label, maxLen) {
  if (!label) return '';
  return label.length > maxLen ? `${label.slice(0, maxLen - 1)}…` : label;
}

function useIsMobile(breakpoint = 700) {
  const query = `(max-width: ${breakpoint}px)`;
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return isMobile;
}

function getMatrixConfig(familyKey, current) {
  const inputs = current?.inputs || {};

  if (familyKey === 'growth_inflation') {
    return {
      layout: 'quad',
      x: normalizeSigned(inputs.growth_delta_pct, 2.5),
      y: normalizeSigned(inputs.inflation_delta_pct, 2.5),
      xLeft: 'Growth Down',
      xRight: 'Growth Up',
      yBottom: 'Inflation Down',
      yTop: 'Inflation Up',
      quadrants: {
        topLeft: 'Stagflation',
        topRight: 'Reflation',
        bottomLeft: 'Disinflationary Slowdown',
        bottomRight: 'Goldilocks',
      },
    };
  }

  if (familyKey === 'volatility_risk') {
    const trendGapPct = Number(inputs.trend_gap_50_pct);
    const trendGap = Number.isFinite(trendGapPct)
      ? trendGapPct
      : ((Number(inputs.spy_close) / Number(inputs.sma_50)) - 1) * 100;
    const vixLevel = Number(inputs.vix);
    const volGap = Number.isFinite(vixLevel) ? vixLevel - 20 : 0;

    return {
      layout: 'quad',
      x: normalizeSigned(trendGap, 12),
      y: normalizeSigned(volGap, 20),
      xLeft: 'Trend Weak',
      xRight: 'Trend Strong',
      yBottom: 'Vol Low',
      yTop: 'Vol High',
      quadrants: {
        topLeft: 'Stress Event',
        topRight: 'Fragile Rally',
        bottomLeft: 'Volatility Warning',
        bottomRight: 'Calm Risk-On',
      },
    };
  }

  return {
    layout: 'six',
    x: normalizeSigned(-(inputs.fed_funds_delta_pct ?? 0), 2),
    y: normalizeSigned(inputs.treasury_spread_pct, 2),
    xLeft: 'Tightening',
    xRight: 'Easing',
    yBottom: 'Inverted Curve',
    yTop: 'Steep Curve',
    quadrants: {
      topLeft: 'Restrictive Tightening',
      topRight: 'Liquidity Expansion',
      bottomLeft: 'Late-Cycle Tightening',
      bottomRight: 'Crisis Easing',
    },
    cells: {
      topLeft: 'Restrictive Tightening',
      topMid: 'Neutral Liquidity',
      topRight: 'Liquidity Expansion',
      bottomLeft: 'Late-Cycle Tightening',
      bottomMid: 'Restrictive Hold',
      bottomRight: 'Crisis Easing',
    },
  };
}

function SnapshotMatrix({ familyKey, family, current, color, isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const [playIndex, setPlayIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);
  const history = family?.history || [];
  const activePoint = (expanded && history.length > 0) ? history[playIndex] : current;
  const config = getMatrixConfig(familyKey, activePoint);
  const dotLeft = `${config.x * 100}%`;
  const dotTop = `${(1 - config.y) * 100}%`;
  const isSixZone = config.layout === 'six';

  useEffect(() => {
    if (!expanded || history.length === 0) return;
    setPlayIndex(history.length - 1);
    setPlaying(false);
  }, [expanded, history.length]);

  useEffect(() => {
    if (!expanded || !playing || history.length <= 1) return;
    const frameMs = Math.max(60, Math.round(260 / playSpeed));
    const timer = window.setInterval(() => {
      setPlayIndex((idx) => {
        if (idx >= history.length - 1) {
          setPlaying(false);
          return idx;
        }
        return idx + 1;
      });
    }, frameMs);
    return () => window.clearInterval(timer);
  }, [expanded, playing, history.length, playSpeed]);

  // Three size tiers: mobile-compact (inline card on a phone), compact (inline
  // card on desktop), and expanded (the zoomed-in modal, which itself shrinks
  // its paddings a bit on mobile since 96vw of a phone is still a narrow box).
  const MatrixBody = ({ compact = true }) => {
    const yColWidth = isMobile ? (compact ? 38 : 48) : (compact ? 52 : 62);
    const bodyPadding = isMobile
      ? (compact ? '14px 8px 26px 42px' : '18px 10px 30px 52px')
      : (compact ? '18px 14px 32px 60px' : '24px 18px 40px 76px');
    const cellFontSize = isMobile ? (compact ? 7.5 : 9) : (compact ? 8.5 : 10);
    const quadFontSize = isMobile ? (compact ? 8 : 9.5) : (compact ? 9 : 11);
    const yLabelFontSize = isMobile ? (compact ? 8.5 : 10) : (compact ? 10 : 12);
    const xLabelFontSize = isMobile ? (compact ? 8.5 : 10) : (compact ? 10 : 12);

    return (
    <div style={{ position: 'relative', padding: bodyPadding }}>
      <div style={{ position: 'relative', aspectRatio: compact ? '1.12 / 1' : '1.45 / 1', borderRadius: 12, overflow: 'hidden', background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05), rgba(255,255,255,0.015))', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(245,158,11,0.05) 45%, rgba(34,197,94,0.08))' }} />

        {isSixZone ? (
          <>
            <div style={{ position: 'absolute', left: '33.333%', top: 12, bottom: 12, width: 1, background: 'rgba(255,255,255,0.2)' }} />
            <div style={{ position: 'absolute', left: '66.666%', top: 12, bottom: 12, width: 1, background: 'rgba(255,255,255,0.2)' }} />
          </>
        ) : (
          <div style={{ position: 'absolute', left: '50%', top: 12, bottom: 12, width: 1, background: 'rgba(255,255,255,0.2)' }} />
        )}
        <div style={{ position: 'absolute', top: '50%', left: 12, right: 12, height: 1, background: 'rgba(255,255,255,0.2)' }} />

        {isSixZone ? (
          <>
            <div style={{ position: 'absolute', top: 14, left: 10, width: '29%', fontSize: cellFontSize, color: 'var(--muted)', lineHeight: 1.15, fontWeight: 600 }}>
              {config.cells.topLeft}
            </div>
            <div style={{ position: 'absolute', top: 14, left: '35.5%', width: '29%', fontSize: cellFontSize, color: 'var(--muted)', lineHeight: 1.15, textAlign: 'center', fontWeight: 600 }}>
              {config.cells.topMid}
            </div>
            <div style={{ position: 'absolute', top: 14, right: 10, width: '29%', fontSize: cellFontSize, color: 'var(--muted)', lineHeight: 1.15, textAlign: 'right', fontWeight: 600 }}>
              {config.cells.topRight}
            </div>

            <div style={{ position: 'absolute', bottom: 14, left: 10, width: '29%', fontSize: cellFontSize, color: 'var(--muted)', lineHeight: 1.15, fontWeight: 600 }}>
              {config.cells.bottomLeft}
            </div>
            <div style={{ position: 'absolute', bottom: 14, left: '35.5%', width: '29%', fontSize: cellFontSize, color: 'var(--muted)', lineHeight: 1.15, textAlign: 'center', fontWeight: 600 }}>
              {config.cells.bottomMid}
            </div>
            <div style={{ position: 'absolute', bottom: 14, right: 10, width: '29%', fontSize: cellFontSize, color: 'var(--muted)', lineHeight: 1.15, textAlign: 'right', fontWeight: 600 }}>
              {config.cells.bottomRight}
            </div>
          </>
        ) : (
          <>
            <div style={{ position: 'absolute', top: 14, left: 14, maxWidth: '34%', fontSize: quadFontSize, color: 'var(--muted)', lineHeight: 1.2, fontWeight: 600 }}>
              {config.quadrants.topLeft}
            </div>
            <div style={{ position: 'absolute', top: 14, right: 14, maxWidth: '34%', fontSize: quadFontSize, color: 'var(--muted)', lineHeight: 1.2, textAlign: 'right', fontWeight: 600 }}>
              {config.quadrants.topRight}
            </div>
            <div style={{ position: 'absolute', bottom: 14, left: 14, maxWidth: '34%', fontSize: quadFontSize, color: 'var(--muted)', lineHeight: 1.2, fontWeight: 600 }}>
              {config.quadrants.bottomLeft}
            </div>
            <div style={{ position: 'absolute', bottom: 14, right: 14, maxWidth: '34%', fontSize: quadFontSize, color: 'var(--muted)', lineHeight: 1.2, textAlign: 'right', fontWeight: 600 }}>
              {config.quadrants.bottomRight}
            </div>
          </>
        )}

        <div style={{ position: 'absolute', left: dotLeft, top: dotTop, transform: 'translate(-50%, -50%)' }}>
          <div style={{ width: compact ? 22 : 28, height: compact ? 22 : 28, borderRadius: 999, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: compact ? 12 : 14, height: compact ? 12 : 14, borderRadius: 999, background: color, boxShadow: `0 0 0 3px ${color}33, 0 0 14px ${color}66`, border: '2px solid rgba(255,255,255,0.95)' }} />
          </div>
        </div>
      </div>

      <div style={{ position: 'absolute', left: isMobile ? 2 : (compact ? 4 : 8), top: compact ? 18 : 24, bottom: compact ? 34 : 40, width: yColWidth, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: yLabelFontSize, color: 'var(--muted)', fontWeight: 600, textAlign: 'center', lineHeight: 1.15 }}>{config.yTop}</span>
        <span style={{ fontSize: compact ? 11 : 13, color: 'var(--muted)' }}>↑</span>
        <span style={{ fontSize: yLabelFontSize, color: 'var(--muted)', fontWeight: 600, textAlign: 'center', lineHeight: 1.15 }}>{config.yBottom}</span>
      </div>
      <div style={{ marginTop: compact ? 8 : 10, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', fontSize: xLabelFontSize, color: 'var(--muted)', fontWeight: 600 }}>
        <span style={{ textAlign: 'left' }}>{config.xLeft}</span>
        <span style={{ padding: compact ? '0 8px' : '0 10px' }}>→</span>
        <span style={{ textAlign: 'right' }}>{config.xRight}</span>
      </div>
    </div>
    );
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>Current Snapshot</div>
      <button
        onClick={() => setExpanded(true)}
        style={{ width: '100%', background: 'transparent', border: 'none', padding: 0, margin: 0, cursor: 'zoom-in', textAlign: 'left' }}
      >
        <MatrixBody compact />
      </button>

      {expanded && (
        <div
          onClick={() => {
            setExpanded(false);
            setPlaying(false);
          }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 10 : 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(1100px, 96vw)', maxHeight: '92vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: isMobile ? 12 : 16, boxShadow: '0 20px 80px rgba(0,0,0,0.4)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Expanded Snapshot</div>
              <button
                onClick={() => {
                  setExpanded(false);
                  setPlaying(false);
                }}
                style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <button
                onClick={() => {
                  if (history.length <= 1) return;
                  setPlaying((p) => {
                    if (p) return false;
                    if (playIndex >= history.length - 1) {
                      setPlayIndex(0);
                    }
                    return true;
                  });
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: history.length > 1 ? 'pointer' : 'not-allowed',
                  opacity: history.length > 1 ? 1 : 0.5,
                }}
              >
                {playing ? 'Pause' : 'Play'}
              </button>

              <input
                type="range"
                min={0}
                max={Math.max(0, history.length - 1)}
                value={playIndex}
                onChange={(e) => {
                  setPlaying(false);
                  setPlayIndex(Number(e.target.value));
                }}
                style={isMobile ? { flexBasis: '100%', minWidth: 140 } : { flex: 1, minWidth: 220 }}
                disabled={history.length <= 1}
              />

              <select
                value={playSpeed}
                onChange={(e) => setPlaySpeed(Number(e.target.value))}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  borderRadius: 8,
                  padding: '6px 8px',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {PLAYBACK_SPEED_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} style={{ color: '#111' }}>{opt.label}</option>
                ))}
              </select>

              <div style={{ fontSize: 12, color: 'var(--muted)', minWidth: isMobile ? 80 : 120, textAlign: 'right' }}>
                {history[playIndex]?.date || current?.date || 'n/a'}
              </div>
            </div>

            <MatrixBody compact={false} />
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyTooltip({ active, payload, label, regimeByIndex, color }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload || {};
  const regime = regimeByIndex[point.regime_index] || point.regime || 'Unknown';
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, minWidth: 240 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontWeight: 700, marginTop: 4, color }}>{regime}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
        Confidence: {point.confidence != null ? formatProbabilityPercent(point.confidence) : 'n/a'}
      </div>
      {point.probabilities && (
        <div style={{ fontSize: 12, marginTop: 6, display: 'grid', gap: 4 }}>
          {Object.entries(point.probabilities)
            .sort((a, b) => Number(b[1]) - Number(a[1]))
            .slice(0, 4)
            .map(([name, prob]) => (
              <div key={name} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ color: 'var(--muted)' }}>{name}</span>
                <span>{formatProbabilityPercent(prob)}</span>
              </div>
            ))}
        </div>
      )}
      {point.reason && <div style={{ fontSize: 12, marginTop: 6 }}>{point.reason}</div>}
    </div>
  );
}

function buildChartState(family) {
  const supportedRegimes = family?.supported_regimes || [];
  const known = [...supportedRegimes];
  const indexByRegime = {};
  known.forEach((name, idx) => {
    indexByRegime[name] = idx;
  });

  (family?.history || []).forEach((item) => {
    if (indexByRegime[item.regime] == null) {
      indexByRegime[item.regime] = known.length;
      known.push(item.regime);
    }
  });

  const regimeByIndex = {};
  Object.keys(indexByRegime).forEach((name) => {
    regimeByIndex[indexByRegime[name]] = name;
  });

  const chartData = (family?.history || []).map((item) => ({
    date: item.date,
    regime: item.regime,
    regime_index: indexByRegime[item.regime],
    reason: item.reason,
    confidence: item.confidence,
    probabilities: item.probabilities || null,
  }));

  return { chartData, indexByRegime, regimeByIndex };
}

function explainModelReason(reason) {
  if (!reason) return 'No model metadata returned.';
  const map = {
    hmmlearn_not_installed: 'hmmlearn is not installed in the running backend environment.',
    no_data: 'Insufficient data was available for model input.',
    misaligned_inputs: 'Feature and label history were misaligned.',
    insufficient_history: 'Not enough lookback history for stable HMM fitting.',
    all_features_nan: 'Model features resolved to empty/NaN values.',
    alignment_failed: 'Date alignment failed while preparing model features.',
    too_few_samples_for_states: 'Too few samples for selected hidden-state count.',
    fit_failed: 'HMM fit failed for this family and window.',
    growth_inputs_unavailable: 'Growth/CPI inputs are unavailable for this timescale.',
    liquidity_inputs_unavailable: 'Liquidity inputs are unavailable for this timescale.',
    not_computed: 'HMM model was not computed for this payload.',
  };
  return map[reason] || reason;
}

function FamilySection({ familyKey, family, isMobile }) {
  const color = FAMILY_COLORS[familyKey] || '#7c8cff';
  const { chartData, regimeByIndex } = useMemo(() => buildChartState(family), [family]);
  const current = family?.current || null;
  const recentChanges = (family?.changes || []).slice(-10).reverse();
  const inputRows = current?.inputs ? Object.entries(current.inputs) : [];
  const probabilityRows = current?.probabilities
    ? Object.entries(current.probabilities).sort((a, b) => Number(b[1]) - Number(a[1]))
    : [];
  const hmmEnabled = Boolean(family?.probability_model?.enabled);
  const modelReason = explainModelReason(family?.probability_model?.reason);
  const confidenceLabel = hmmEnabled ? 'HMM Confidence' : 'Confidence';
  const confidenceHint = hmmEnabled
    ? 'Max posterior probability from Gaussian HMM'
    : 'Rule-based heuristic score';
  const topProbabilityRow = probabilityRows[0] || null;

  return (
    <section style={{ border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: isMobile ? 12 : 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700 }}>{family?.label}</div>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--muted)' }}>{family?.description}</div>
          <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 700, color: hmmEnabled ? '#22c55e' : '#f59e0b' }}>
            <span>{hmmEnabled ? 'Model: HMM' : 'Model: Heuristic'}</span>
          </div>
          {!hmmEnabled && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
              HMM unavailable: {modelReason}
            </div>
          )}
        </div>
        <div style={{ fontSize: 12, color: family?.status === 'available' ? '#22c55e' : '#f59e0b', textTransform: 'uppercase', fontWeight: 700 }}>
          {family?.status || 'unknown'}
        </div>
      </div>

      {!current && (
        <div style={{ marginTop: 14, color: 'var(--muted)', fontSize: 13 }}>
          This matrix is currently unavailable.
          {(family?.missing_inputs || []).length > 0 ? ` Missing inputs: ${family.missing_inputs.join(', ')}` : ''}
        </div>
      )}

      {current && (
        <>
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '100%' : '220px'}, 1fr))`, gap: 12 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Current Regime (rule-based)</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color }}>{current.regime}</div>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>{current.reason}</div>
            </div>
            {hmmEnabled && topProbabilityRow && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>HMM Most Likely Panel</div>
                <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color }}>
                  {topProbabilityRow[0]}
                </div>
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>
                  {formatProbabilityPercent(topProbabilityRow[1])} posterior probability
                </div>
              </div>
            )}
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{confidenceLabel}</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>
                {current.confidence != null ? formatProbabilityPercent(current.confidence) : 'n/a'}
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>As of {current.date}</div>
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--muted)' }}>{confidenceHint}</div>
            </div>
            {probabilityRows.length > 0 && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>HMM Panel Probabilities</div>
                <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                  {probabilityRows.map(([name, prob]) => {
                    const pctNumeric = Math.max(0, Math.min(99.9, Number(prob) * 100));
                    return (
                      <div key={name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: 'var(--muted)' }}>{name}</span>
                          <span>{formatProbabilityPercent(prob)}</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.09)', overflow: 'hidden' }}>
                          <div style={{ width: `${pctNumeric}%`, height: '100%', background: color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>
                  Model: Gaussian HMM posterior distribution
                </div>
              </div>
            )}
            <SnapshotMatrix familyKey={familyKey} family={family} current={current} color={color} isMobile={isMobile} />
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, gridColumn: isMobile ? 'auto' : 'span 2' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Current Inputs</div>
              <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '100%' : '180px'}, 1fr))`, gap: 8 }}>
                {inputRows.map(([name, value]) => (
                  <div key={name} style={{ fontSize: 12 }}>
                    <span style={{ color: 'var(--muted)' }}>{name.replace(/_/g, ' ')}:</span> {value == null ? 'n/a' : String(value)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, width: '100%', height: isMobile ? 280 : 320 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: isMobile ? 0 : 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: isMobile ? 9 : 11, fill: 'var(--muted)' }} minTickGap={isMobile ? 32 : 22} />
                <YAxis
                  type="number"
                  domain={[0, Math.max(0, Object.keys(regimeByIndex).length - 1)]}
                  ticks={Object.keys(regimeByIndex).map((key) => Number(key))}
                  tickFormatter={(tick) => truncateLabel(regimeByIndex[tick] || '', isMobile ? 11 : 30)}
                  tick={{ fontSize: isMobile ? 9 : 11, fill: 'var(--muted)' }}
                  width={isMobile ? 88 : 170}
                />
                <Tooltip content={<FamilyTooltip regimeByIndex={regimeByIndex} color={color} />} />
                <Line type="stepAfter" dataKey="regime_index" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
                {recentChanges.map((chg) => (
                  <ReferenceLine key={`${familyKey}-${chg.date}-${chg.to}`} x={chg.date} stroke="rgba(255,255,255,0.18)" strokeDasharray="4 4" />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="family-detail-grid" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, overflowX: 'auto' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Recent Changes</div>
              {recentChanges.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13 }}>No changes in the selected lookback.</div>}
              {recentChanges.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 11 }}>
                      <th style={{ padding: isMobile ? '6px 4px' : '8px 6px' }}>Date</th>
                      <th style={{ padding: isMobile ? '6px 4px' : '8px 6px' }}>From</th>
                      <th style={{ padding: isMobile ? '6px 4px' : '8px 6px' }}>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentChanges.map((chg) => (
                      <tr key={`${familyKey}-${chg.date}-${chg.to}-${chg.from || 'start'}`} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ padding: isMobile ? '8px 4px' : '10px 6px', fontSize: isMobile ? 12 : 13, whiteSpace: 'nowrap' }}>{chg.date}</td>
                        <td style={{ padding: isMobile ? '8px 4px' : '10px 6px', fontSize: isMobile ? 12 : 13, color: 'var(--muted)' }}>{chg.from || 'Start'}</td>
                        <td style={{ padding: isMobile ? '8px 4px' : '10px 6px', fontSize: isMobile ? 12 : 13, color }}>{chg.to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Supported States</div>
              {(family?.supported_regimes || []).map((name) => (
                <div key={name} style={{ fontSize: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: color, display: 'inline-block' }} />
                  <span>{name}</span>
                </div>
              ))}
              {(family?.missing_inputs || []).length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Missing Inputs</div>
                  <div style={{ fontSize: 12, color: '#f59e0b' }}>{family.missing_inputs.join(', ')}</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default function RegimeTrackerPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timescaleDays, setTimescaleDays] = useState(365);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    getHomeMarketRegime(timescaleDays)
      .then((res) => {
        if (!mounted) return;
        setPayload(res.data || null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.data?.detail || 'Failed to load regime tracker data.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [timescaleDays]);

  const families = payload?.families || {};
  const familyKeys = ['growth_inflation', 'volatility_risk', 'liquidity'];

  return (
    <div style={{ maxWidth: 1360, margin: '0 auto', padding: isMobile ? '14px 12px 24px' : '24px 24px 30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 30, letterSpacing: -0.3 }}>Regime Tracker</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: isMobile ? 13 : 14 }}>
            Growth/inflation, volatility risk, and monetary-policy/liquidity regimes with historical transitions.
          </p>
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: 'pointer',
            fontWeight: 600,
            minHeight: 40,
          }}
        >
          Back to Home
        </button>
      </div>

      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 4 }}>Timescale:</span>
        {TIMESCALE_OPTIONS.map((option) => {
          const active = timescaleDays === option.days;
          return (
            <button
              key={option.days}
              onClick={() => setTimescaleDays(option.days)}
              style={{
                background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                borderRadius: 8,
                padding: isMobile ? '8px 12px' : '6px 10px',
                fontSize: 12,
                fontWeight: active ? 700 : 600,
                cursor: 'pointer',
                minHeight: isMobile ? 36 : 'auto',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <section style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: isMobile ? 12 : 16 }}>
        {loading && <div style={{ color: 'var(--muted)' }}>Loading regime data...</div>}
        {!loading && error && <div style={{ color: '#ef4444' }}>{error}</div>}
        {!loading && !error && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '100%' : '240px'}, 1fr))`, gap: 12 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Snapshot</div>
              <div style={{ marginTop: 4, fontWeight: 700, fontSize: isMobile ? 18 : 22 }}>{payload?.summary?.title || 'Macro Regime Snapshot'}</div>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>{payload?.summary?.description || 'No summary available.'}</div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Available Inputs</div>
              <div style={{ marginTop: 8, fontSize: 13 }}>{(payload?.available_inputs || []).join(', ') || 'None'}</div>
            </div>
          </div>
        )}
      </section>

      {!loading && !error && (
        <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
          {familyKeys.map((key) => (
            <FamilySection key={key} familyKey={key} family={families[key]} isMobile={isMobile} />
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 1120px) {
          .family-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
